import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import PhoneInput from '../components/common/PhoneInput';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { isValidSiret, normalizeSiret, SIRET_ERROR } from '../utils/siret';

export default function EditProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    typePersonne: 'personne_physique',
    designation: '',
    raisonSociale: '',
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    telephoneMobile: '',
    adresse: {
      rue: '',
      complement: '',
      codePostal: '',
      ville: '',
      pays: 'France'
    },
    dateNaissance: '',
    napi: '',
    numeroAmexa: '',
    siret: ''
  });

  // Options pour le type de personne
  const typePersonneOptions = [
    { value: 'personne_physique', label: 'Personne physique' },
    { value: 'association', label: 'Association' },
    { value: 'scea', label: 'SCEA' },
    { value: 'earl', label: 'EARL' },
    { value: 'etablissement_public', label: 'Établissement public' },
  ];

  // Vérifier si c'est une personne morale
  const isPersonneMorale = ['association', 'scea', 'earl', 'etablissement_public'].includes(formData.typePersonne);

  // Labels dynamiques
  const nomLabel = isPersonneMorale ? 'Nom du représentant légal' : 'Nom';
  const prenomLabel = isPersonneMorale ? 'Prénom du représentant légal' : 'Prénom';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [sendingContact, setSendingContact] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchUserData();
  }, [user, navigate]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData({
        typePersonne: response.data.personalInfo?.typePersonne || 'personne_physique',
        designation: response.data.personalInfo?.designation || '',
        raisonSociale: response.data.personalInfo?.raisonSociale || '',
        prenom: response.data.prenom || '',
        nom: response.data.nom || '',
        email: response.data.email || '',
        telephone: response.data.personalInfo?.telephone || '',
        telephoneMobile: response.data.personalInfo?.telephoneMobile || '',
        adresse: {
          rue: response.data.personalInfo?.adresse?.rue || '',
          complement: response.data.personalInfo?.adresse?.complement || '',
          codePostal: response.data.personalInfo?.adresse?.codePostal || '',
          ville: response.data.personalInfo?.adresse?.ville || '',
          pays: response.data.personalInfo?.adresse?.pays || 'France'
        },
        dateNaissance: response.data.personalInfo?.dateNaissance 
          ? new Date(response.data.personalInfo.dateNaissance).toISOString().split('T')[0]
          : '',
        napi: response.data.napi || '',
        numeroAmexa: response.data.numeroAmexa || '',
        siret: response.data.siret || ''
      });
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else if (name === 'siret') {
      setFormData(prev => ({ ...prev, siret: normalizeSiret(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSendContactMessage = async () => {
    if (!contactMessage.trim()) return;
    
    setSendingContact(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/contact-admin`, {
        message: contactMessage,
        subject: 'Demande de modification de profil'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContactSuccess(true);
      setContactMessage('');
      setTimeout(() => {
        setShowContactModal(false);
        setContactSuccess(false);
      }, 2000);
    } catch (err) {
      setError('Erreur lors de l\'envoi du message');
    } finally {
      setSendingContact(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isValidSiret(formData.siret, { required: false })) {
      setError(SIRET_ERROR);
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${process.env.REACT_APP_API_URL}/auth/profile`, {
        typePersonne: formData.typePersonne,
        designation: formData.designation,
        raisonSociale: formData.raisonSociale,
        prenom: formData.prenom,
        nom: formData.nom,
        email: formData.email,
        telephone: formData.telephone,
        telephoneMobile: formData.telephoneMobile,
        adresse: formData.adresse,
        dateNaissance: formData.dateNaissance,
        napi: formData.napi,
        numeroAmexa: formData.numeroAmexa,
        siret: formData.siret
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Profil mis à jour avec succès');
      setTimeout(() => {
        if (user?.role === 'admin' || user?.role === 'super_admin') {
          navigate('/admin/dashboard');
        } else {
          navigate('/dashboard');
        }
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Modifier mon profil</h1>
        <p className="mt-2 text-sm text-slate-600">
          Mettez à jour vos informations personnelles
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-lg bg-green-50 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6">
            {/* Encart d'information pour les champs non modifiables */}
            <div className="rounded-lg bg-honey-50 border border-honey-200 p-4">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-honey-500 flex-shrink-0" />
                <div className="ml-3">
                  <p className="text-sm text-honey-700">
                    Les informations d'identité (type, civilité, nom, prénom, raison sociale) ne sont pas modifiables directement. 
                    Pour toute modification, veuillez{' '}
                    <button
                      type="button"
                      onClick={() => setShowContactModal(true)}
                      className="font-medium text-honey-800 underline hover:text-honey-900"
                    >
                      contacter l'administrateur
                    </button>.
                  </p>
                </div>
              </div>
            </div>

            {/* Type de personne et Civilité - DÉSACTIVÉS */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="typePersonne" className="block text-sm font-medium text-slate-500">
                  Type
                </label>
                <select
                  name="typePersonne"
                  id="typePersonne"
                  value={formData.typePersonne}
                  disabled
                  className="mt-1 block w-full border border-slate-200 rounded-lg shadow-sm py-2 px-3 bg-slate-100 text-slate-500 cursor-not-allowed sm:text-sm"
                >
                  {typePersonneOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="designation" className="block text-sm font-medium text-slate-500">
                  Civilité
                </label>
                <select
                  name="designation"
                  id="designation"
                  value={formData.designation}
                  disabled
                  className="mt-1 block w-full border border-slate-200 rounded-lg shadow-sm py-2 px-3 bg-slate-100 text-slate-500 cursor-not-allowed sm:text-sm"
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="M.">M.</option>
                  <option value="Mme">Mme</option>
                </select>
              </div>
            </div>

            {/* Raison sociale - visible uniquement pour personne morale - DÉSACTIVÉ */}
            {isPersonneMorale && (
              <div>
                <label htmlFor="raisonSociale" className="block text-sm font-medium text-slate-500">
                  Raison sociale
                </label>
                <input
                  type="text"
                  name="raisonSociale"
                  id="raisonSociale"
                  value={formData.raisonSociale}
                  disabled
                  className="mt-1 block w-full border border-slate-200 rounded-lg shadow-sm py-2 px-3 bg-slate-100 text-slate-500 cursor-not-allowed sm:text-sm"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="prenom" className="block text-sm font-medium text-slate-500">
                  {prenomLabel}
                </label>
                <input
                  type="text"
                  name="prenom"
                  id="prenom"
                  value={formData.prenom}
                  disabled
                  className="mt-1 block w-full border border-slate-200 rounded-lg shadow-sm py-2 px-3 bg-slate-100 text-slate-500 cursor-not-allowed sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="nom" className="block text-sm font-medium text-slate-500">
                  {nomLabel}
                </label>
                <input
                  type="text"
                  name="nom"
                  id="nom"
                  value={formData.nom}
                  disabled
                  className="mt-1 block w-full border border-slate-200 rounded-lg shadow-sm py-2 px-3 bg-slate-100 text-slate-500 cursor-not-allowed sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-slate-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-honey-500 focus:border-honey-500 sm:text-sm"
              />
            </div>

              <div>
                <label htmlFor="telephoneMobile" className="block text-sm font-medium text-slate-700">
                  Téléphone mobile
                </label>
                <div className="mt-1">
                  <PhoneInput
                    name="telephoneMobile"
                    id="telephoneMobile"
                    value={formData.telephoneMobile || ''}
                    onChange={(value) => setFormData(prev => ({ ...prev, telephoneMobile: value }))}
                    placeholder=""
                  />
                </div>
              </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="telephone" className="block text-sm font-medium text-slate-700">
                  Téléphone fixe
                </label>
                <div className="mt-1">
                  <PhoneInput
                    name="telephone"
                    id="telephone"
                    value={formData.telephone || ''}
                    onChange={(value) => setFormData(prev => ({ ...prev, telephone: value }))}
                    placeholder=""
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="adresse.rue" className="block text-sm font-medium text-slate-700">
                Adresse
              </label>
              <div className="mt-1">
                <AddressAutocomplete
                  initialValue={formData.adresse.rue}
                  placeholder="Commencez à taper votre adresse..."
                  inputClassName="block w-full border border-slate-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-honey-500 focus:border-honey-500 sm:text-sm"
                  onAddressSelect={(address) => {
                    setFormData(prev => ({
                      ...prev,
                      adresse: {
                        ...prev.adresse,
                        rue: address.rue,
                        codePostal: address.codePostal,
                        ville: address.ville
                      }
                    }));
                  }}
                />
              </div>
            </div>

            <div>
              <label htmlFor="adresse.complement" className="block text-sm font-medium text-slate-700">
                Complément d'adresse
              </label>
              <input
                type="text"
                name="adresse.complement"
                id="adresse.complement"
                value={formData.adresse.complement}
                onChange={handleChange}
                placeholder="Bâtiment, étage, appartement..."
                className="mt-1 block w-full border border-slate-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-honey-500 focus:border-honey-500 sm:text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="adresse.codePostal" className="block text-sm font-medium text-slate-700">
                  Code postal
                </label>
                <input
                  type="text"
                  name="adresse.codePostal"
                  id="adresse.codePostal"
                  value={formData.adresse.codePostal}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-slate-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-honey-500 focus:border-honey-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="adresse.ville" className="block text-sm font-medium text-slate-700">
                  Ville
                </label>
                <input
                  type="text"
                  name="adresse.ville"
                  id="adresse.ville"
                  value={formData.adresse.ville}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-slate-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-honey-500 focus:border-honey-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="adresse.pays" className="block text-sm font-medium text-slate-700">
                Pays
              </label>
              <input
                type="text"
                name="adresse.pays"
                id="adresse.pays"
                value={formData.adresse.pays}
                onChange={handleChange}
                className="mt-1 block w-full border border-slate-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-honey-500 focus:border-honey-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="dateNaissance" className="block text-sm font-medium text-slate-700">
                Date de naissance
              </label>
              <input
                type="date"
                name="dateNaissance"
                id="dateNaissance"
                value={formData.dateNaissance}
                onChange={handleChange}
                className="mt-1 block w-full border border-slate-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-honey-500 focus:border-honey-500 sm:text-sm"
              />
            </div>

            {/* Section Informations apicoles */}
            <div className="border-t border-slate-200 pt-6 mt-6">
              <h3 className="text-lg font-medium text-slate-900 mb-4">Informations apicoles</h3>
              <p className="text-sm text-slate-500 mb-4">
                Ces informations seront pré-remplies lors de vos prochaines demandes d'adhésion ou de souscription aux services.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="napi" className="block text-sm font-medium text-slate-700">
                    NAPI (Numéro d'Apiculteur)
                  </label>
                  <input
                    type="text"
                    name="napi"
                    id="napi"
                    value={formData.napi}
                    onChange={handleChange}
                    placeholder="Ex: 974XXXXXX"
                    className="mt-1 block w-full border border-slate-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-honey-500 focus:border-honey-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="numeroAmexa" className="block text-sm font-medium text-slate-700">
                    Numéro AMEXA
                  </label>
                  <input
                    type="text"
                    name="numeroAmexa"
                    id="numeroAmexa"
                    value={formData.numeroAmexa}
                    onChange={handleChange}
                    placeholder="Numéro AMEXA"
                    className="mt-1 block w-full border border-slate-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-honey-500 focus:border-honey-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="siret" className="block text-sm font-medium text-slate-700">
                    SIRET
                  </label>
                  <input
                    type="text"
                    name="siret"
                    id="siret"
                    value={formData.siret}
                    maxLength="14"
                    onChange={handleChange}
                    placeholder="14 chiffres exactement"
                    className="mt-1 block w-full border border-slate-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-honey-500 focus:border-honey-500 sm:text-sm"
                  />
                  {formData.siret && formData.siret.length !== 14 && (
                    <p className="mt-1 text-xs text-red-500">{SIRET_ERROR}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-3 bg-slate-50 text-right sm:px-6 flex justify-between">
          <button
            type="button"
            onClick={() => {
              if (user?.role === 'admin' || user?.role === 'super_admin') {
                navigate('/admin/dashboard');
              } else {
                navigate('/dashboard');
              }
            }}
            className="inline-flex justify-center py-2 px-4 border border-slate-300 shadow-sm text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-honey-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-honey-500 hover:bg-honey-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-honey-500 disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>

      {/* Modal de contact administrateur */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Contacter l'administrateur
              </h3>
              <button
                onClick={() => { setShowContactModal(false); setContactMessage(''); setContactSuccess(false); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              {contactSuccess ? (
                <div className="text-center py-4">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                    <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-green-600 font-medium">Message envoyé avec succès !</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Décrivez les modifications que vous souhaitez apporter à vos informations d'identité. 
                    Un administrateur traitera votre demande dans les plus brefs délais.
                  </p>
                  <textarea
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    placeholder="Décrivez votre demande de modification..."
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-honey-500 focus:border-honey-500 text-sm"
                  />
                  <div className="mt-4 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => { setShowContactModal(false); setContactMessage(''); }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleSendContactMessage}
                      disabled={sendingContact || !contactMessage.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-honey-500 rounded-lg hover:bg-honey-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sendingContact ? 'Envoi...' : 'Envoyer'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
